/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-07-27 11:43:33
 **/

import { useAtomValue } from "jotai";
import { useEffect } from "react";
import { createPortal } from "react-dom";

import { PhoneCallAtoms, usePhoneCall } from "@/frameworks/core/hooks";
import { JotaiStore } from "@/frameworks/libraries/commons";
import { useStompClient } from "@/frameworks/libraries/network";
import { Configure } from "@/frameworks/libraries";

type PhoneBoothProps = {
	callID: string;
};

const LOGTAG = "#PhoneBooth#";

function convert_state_to_object(
	media: Pick<ChatServices.C2C.DeviceMediaState.Data, "audio" | "screenShare" | "video">
) {
	return Object.entries(media).reduce((total, [type, status]) => {
		if (type === "screenShare") {
			total = {
				...total,
				screenShare: status
			};
		} else {
			total = {
				...total,
				[type]: (status as { state: MediaState })?.state
			};
		}
		return total;
	}, {});
}

function PhoneBooth({ callID }: PhoneBoothProps) {
	const phoneCall = usePhoneCall();
	const call = phoneCall.state.call(callID)[0];
	const callUsers = phoneCall.state.users(callID)[0];
	const callStatus = phoneCall.state.status(callID)[0];

	useEffect(() => {
		return () => {
			console.debug(LOGTAG, "PhoneBooth closed");
		};
	}, []);

	useEffect(() => {
		phoneCall.process_call(callID);

		phoneCall.process_caller(callID);
		phoneCall.process_callee(callID);

		phoneCall.process_call_time(callID);
		phoneCall.process_record_call_time(callID);
	}, [call]);

	useEffect(() => {
		if (callStatus === "END") {
			// phoneCall.stream.local_stream = null;
		}
	}, [callStatus]);

	useEffect(() => {
		console.debug(LOGTAG, "callUsers: ", callUsers);
	}, [callUsers]);

	useStompClient(
		{
			onMessageReceived: ({ type, payload }) => {
				switch (type) {
					case "deviceMediaState": {
						const {
							id: userID,
							channelID: callID,
							...media
						} = payload as ChatServices.C2C.DeviceMediaState.Data;

						const old_media = phoneCall.get_user_media(callID, userID);
						const new_media = {
							...old_media,
							...convert_state_to_object({
								// audio: media.audio,
								// screenShare: media.screenShare,
								// video: media.video
								...media
							})
						};
						console.debug(LOGTAG, "device media state", userID, callID, old_media, new_media);
						phoneCall.set_user_media(callID, userID, new_media);
						break;
					}

					case "startScreenShare": {
						const { callID, userID } = payload as ChatServices.C2C.ScreenShareState.Data;
						// 다른 사람이 공유 하려하는데 내가 공유중이면 끄기.
						// 내가 공유중인데 다시 공유하려할때는 xms_client에서 자기 스트림 종료후 다시 시작처리
						if (
							userID !== Configure.userID &&
							phoneCall.get_user_media(callID, Configure.userID).screenShare
						) {
							phoneCall.screen_update(callID, Configure.userID, "stop");
						}
						phoneCall.set_user_media(callID, userID, { screenShare: true });
						break;
					}

					case "stopScreenShare": {
						const { callID, userID } = payload as ChatServices.C2C.ScreenShareState.Data;
						const _userID = userID.replace("screen_", "");
						phoneCall.set_user_media(callID, _userID, { screenShare: false });
						break;
					}
				}
			},
			onWebSocketClosed() {
				const activeCallID = phoneCall.state.active.activeCallID;
				if (activeCallID) {
					phoneCall.cleanup_call({ callID: activeCallID, playEndSound: false, cleanupDelay: false });
				}
			}
		},
		[]
	);

	return null;
}

export default function PhoneBooths() {
	const callList = useAtomValue(PhoneCallAtoms.CallList, { store: JotaiStore });
	return (
		<>
			{createPortal(
				<>
					{Object.keys(callList).map((callID) => (
						<PhoneBooth key={callID} callID={callID} />
					))}
				</>,
				document.body
			)}
		</>
	);
}
